/** @format */

import { Centrifuge, UnauthorizedError } from "centrifuge";

import galaxieClient, { getAccessToken } from "./galaxieClient";

let client: Centrifuge | null = null;

function buildWsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/connection/websocket`;
}

const TOKEN_FETCH_TIMEOUT_MS = 8000;
const TOKEN_RETRY_DELAY_MS = 300;

/** Anonymous users get a token with an empty subject; still time-boxed. */
async function fetchAnonymousToken(): Promise<string> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch("/api/centrifugo/token/", {
      method: "POST",
      signal: controller.signal,
    });
    if (response.status === 401 || response.status === 403) {
      throw new UnauthorizedError("Forbidden");
    }
    if (!response.ok) {
      throw new Error(`Token fetch failed: ${response.status}`);
    }
    const data = (await response.json()) as { token: string };
    return data.token;
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchAuthenticatedToken(): Promise<string> {
  try {
    const response = await galaxieClient.post<{ token: string }>("/centrifugo/token/");
    return response.data.token;
  } catch (error) {
    const status =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    if (status === 401 || status === 403) {
      throw new UnauthorizedError("Forbidden");
    }
    throw error instanceof Error ? error : new Error(String(error));
  }
}

async function getToken(): Promise<string> {
  const fetchOnce = getAccessToken() ? fetchAuthenticatedToken : fetchAnonymousToken;

  // One retry with a short backoff so a transient stall (slow network, brief
  // 5xx, aborted fetch) doesn't surface as a failed connect / token expired.
  // Auth failures (401/403) are surfaced immediately so centrifuge-js stops.
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await fetchOnce();
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        throw error;
      }
      lastError = error;
      if (attempt === 0) {
        await new Promise(resolve => setTimeout(resolve, TOKEN_RETRY_DELAY_MS));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

export function getCentrifugoClient(): Centrifuge {
  if (!client) {
    client = new Centrifuge(buildWsUrl(), { getToken });
    client.connect();
  }
  return client;
}

export function disconnectCentrifugo(): void {
  if (client) {
    client.disconnect();
    client = null;
  }
}
